import { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Check, ChevronDown, Bell, Loader2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { toast } from '../ui/Toast';
import { api } from '../../services/api';
import type { ListingsParams } from '../../services/api';
import { usePushNotifications } from '../../hooks/usePushNotifications';

const CONTRACT_TYPES = [
  { value: 'RENT', label: 'Affitto' },
  { value: 'SALE', label: 'Vendita' },
];

const PRICE_PRESETS_RENT = [
  { label: 'Fino a 500', min: 0, max: 500 },
  { label: '500 - 800', min: 500, max: 800 },
  { label: '800 - 1.200', min: 800, max: 1200 },
  { label: 'Oltre 1.200', min: 1200, max: undefined },
];

const PRICE_PRESETS_SALE = [
  { label: 'Fino a 150k', min: 0, max: 150000 },
  { label: '150k - 250k', min: 150000, max: 250000 },
  { label: '250k - 400k', min: 250000, max: 400000 },
  { label: 'Oltre 400k', min: 400000, max: undefined },
];

const ROOM_PRESETS = [
  { label: 'Mono', value: 1 },
  { label: 'Bilo', value: 2 },
  { label: 'Trilo', value: 3 },
  { label: '4+', value: 4 },
];

const PROPERTY_TYPES = [
  { value: 'APARTMENT', label: 'Appartamento' },
  { value: 'HOUSE', label: 'Casa' },
  { value: 'VILLA', label: 'Villa' },
  { value: 'STUDIO', label: 'Monolocale' },
];

interface NewSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSearchCreated: () => void;
}

function generateSearchName(filters: ListingsParams): string {
  const parts: string[] = [];
  if (filters.contractType === 'RENT') parts.push('Affitto');
  else if (filters.contractType === 'SALE') parts.push('Vendita');
  else parts.push('Tutti');
  parts.push(filters.city || 'Italia');
  if (filters.minPrice || filters.maxPrice) {
    const min = filters.minPrice || 0;
    const max = filters.maxPrice;
    if (max) {
      parts.push(`${min >= 1000 ? `${Math.round(min / 1000)}k` : min}-${max >= 1000 ? `${Math.round(max / 1000)}k` : max} EUR`);
    } else {
      parts.push(`da ${min >= 1000 ? `${Math.round(min / 1000)}k` : min} EUR`);
    }
  }
  return parts.join(' - ');
}

export function NewSearchDialog({ open, onOpenChange, onSearchCreated }: NewSearchDialogProps) {
  const { subscribe: subscribePush, isSubscribed: isPushSubscribed } = usePushNotifications();

  const [filters, setFilters] = useState<ListingsParams>({
    contractType: 'RENT',
    city: 'Milano',
  });
  const [customName, setCustomName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onOpenChange]);

  const isRent = filters.contractType === 'RENT' || !filters.contractType;
  const pricePresets = isRent ? PRICE_PRESETS_RENT : PRICE_PRESETS_SALE;

  const isPricePresetActive = (preset: { min: number; max: number | undefined }) => {
    return filters.minPrice === preset.min && filters.maxPrice === preset.max;
  };

  const isRoomPresetActive = (value: number) => {
    if (value === 4) return filters.minRooms === 4 && !filters.maxRooms;
    return filters.minRooms === value && filters.maxRooms === value;
  };

  const handleSave = useCallback(async () => {
    if (!filters.city?.trim()) {
      toast({ title: 'Inserisci una citta', variant: 'error' });
      return;
    }

    setIsSaving(true);
    try {
      if (!isPushSubscribed) {
        await subscribePush();
      }

      const name = customName.trim() || generateSearchName(filters);
      await api.createSavedSearch({
        name,
        filters,
        notifyEnabled: true,
      });

      toast({ title: 'Ricerca creata con notifiche attive', variant: 'success' });
      onOpenChange(false);
      onSearchCreated();

      // Reset
      setFilters({ contractType: 'RENT', city: 'Milano' });
      setCustomName('');
    } catch (err) {
      console.error('Failed to create search:', err);
      toast({ title: 'Errore nella creazione della ricerca', variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [filters, customName, isPushSubscribed, subscribePush, onOpenChange, onSearchCreated]);

  const Chip = ({ label, isActive, onClick }: {
    label: string; isActive: boolean; onClick: () => void;
  }) => (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2.5 rounded-full text-sm font-medium transition-all min-h-[44px] ${
        isActive
          ? 'bg-blue-500 text-white shadow-sm'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-300'
      }`}
    >
      {label}
    </button>
  );

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0" style={{ zIndex: 1100 }}>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => onOpenChange(false)}
      />

      {/* Panel: fullscreen mobile, centered desktop */}
      <div className="absolute inset-0 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-[480px] md:max-h-[90vh] md:rounded-2xl bg-white flex flex-col md:shadow-xl overflow-hidden">

        {/* Header */}
        <div className="flex-shrink-0 border-b px-4 py-3 flex items-center justify-between safe-top bg-white">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-blue-500" />
            <h2 className="text-lg font-semibold text-gray-900">Nuova ricerca</h2>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            className="h-10 w-10 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
            aria-label="Chiudi"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Search name */}
          <div>
            <label className="text-sm font-semibold text-gray-900 mb-2 block">
              Nome ricerca (opzionale)
            </label>
            <Input
              type="text"
              placeholder={generateSearchName(filters)}
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              className="h-12"
            />
            <p className="text-xs text-gray-400 mt-1">
              Lascia vuoto per generare automaticamente
            </p>
          </div>

          {/* Contract Type */}
          <div>
            <label className="text-sm font-semibold text-gray-900 mb-3 block">Tipo annuncio</label>
            <div className="flex gap-2">
              {CONTRACT_TYPES.map((type) => (
                <Chip
                  key={type.value}
                  label={type.label}
                  isActive={filters.contractType === type.value}
                  onClick={() => setFilters({ ...filters, contractType: type.value, minPrice: undefined, maxPrice: undefined })}
                />
              ))}
            </div>
          </div>

          {/* City */}
          <div>
            <label className="text-sm font-semibold text-gray-900 mb-3 block">Citta *</label>
            <Input
              type="text"
              placeholder="Es. Milano, Roma, Napoli..."
              value={filters.city || ''}
              onChange={(e) => setFilters({ ...filters, city: e.target.value || undefined })}
              className="h-12"
            />
          </div>

          {/* Budget */}
          <div>
            <label className="text-sm font-semibold text-gray-900 mb-3 block">Budget</label>
            <div className="flex flex-wrap gap-2">
              <Chip
                label="Qualsiasi"
                isActive={!filters.minPrice && !filters.maxPrice}
                onClick={() => setFilters({ ...filters, minPrice: undefined, maxPrice: undefined })}
              />
              {pricePresets.map((preset) => (
                <Chip
                  key={preset.label}
                  label={preset.label}
                  isActive={isPricePresetActive(preset)}
                  onClick={() => setFilters({ ...filters, minPrice: preset.min, maxPrice: preset.max })}
                />
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Input
                type="number"
                placeholder="Min"
                min={0}
                value={filters.minPrice || ''}
                className="h-12"
                onChange={(e) => setFilters({ ...filters, minPrice: e.target.value ? parseInt(e.target.value) : undefined })}
              />
              <Input
                type="number"
                placeholder="Max"
                min={0}
                value={filters.maxPrice || ''}
                className="h-12"
                onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value ? parseInt(e.target.value) : undefined })}
              />
            </div>
          </div>

          {/* Rooms */}
          <div>
            <label className="text-sm font-semibold text-gray-900 mb-3 block">Locali</label>
            <div className="flex flex-wrap gap-2">
              <Chip
                label="Tutti"
                isActive={!filters.minRooms && !filters.maxRooms}
                onClick={() => setFilters({ ...filters, minRooms: undefined, maxRooms: undefined })}
              />
              {ROOM_PRESETS.map((preset) => (
                <Chip
                  key={preset.label}
                  label={preset.label}
                  isActive={isRoomPresetActive(preset.value)}
                  onClick={() => {
                    if (preset.value === 4) setFilters({ ...filters, minRooms: 4, maxRooms: undefined });
                    else setFilters({ ...filters, minRooms: preset.value, maxRooms: preset.value });
                  }}
                />
              ))}
            </div>
          </div>

          {/* Property Type */}
          <div>
            <label className="text-sm font-semibold text-gray-900 mb-3 block">Tipo immobile</label>
            <div className="flex flex-wrap gap-2">
              <Chip
                label="Tutti"
                isActive={!filters.propertyType}
                onClick={() => setFilters({ ...filters, propertyType: undefined })}
              />
              {PROPERTY_TYPES.map((type) => (
                <Chip
                  key={type.value}
                  label={type.label}
                  isActive={filters.propertyType === type.value}
                  onClick={() => setFilters({ ...filters, propertyType: type.value })}
                />
              ))}
            </div>
          </div>

          {/* Advanced features */}
          <details className="group">
            <summary className="text-sm font-semibold text-gray-900 cursor-pointer list-none flex items-center justify-between py-2">
              Altre caratteristiche
              <ChevronDown className="w-5 h-5 text-gray-400 group-open:rotate-180 transition-transform" />
            </summary>
            <div className="pt-3 space-y-3">
              <div>
                <label className="text-sm text-gray-600 mb-2 block">Superficie (m2)</label>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    type="number"
                    placeholder="Min"
                    min={0}
                    value={filters.minSurface || ''}
                    className="h-12"
                    onChange={(e) => setFilters({ ...filters, minSurface: e.target.value ? parseInt(e.target.value) : undefined })}
                  />
                  <Input
                    type="number"
                    placeholder="Max"
                    min={0}
                    value={filters.maxSurface || ''}
                    className="h-12"
                    onChange={(e) => setFilters({ ...filters, maxSurface: e.target.value ? parseInt(e.target.value) : undefined })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: 'hasElevator', label: 'Ascensore' },
                  { key: 'hasTerrace', label: 'Terrazzo' },
                  { key: 'isFurnished', label: 'Arredato' },
                  { key: 'hasGarden', label: 'Giardino' },
                ].map((feature) => (
                  <label
                    key={feature.key}
                    className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all min-h-[52px] ${
                      (filters as any)[feature.key]
                        ? 'bg-blue-50 border-2 border-blue-500'
                        : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={(filters as any)[feature.key] || false}
                      className="sr-only"
                      onChange={(e) => setFilters({ ...filters, [feature.key]: e.target.checked || undefined } as any)}
                    />
                    <span className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                      (filters as any)[feature.key] ? 'bg-blue-500 text-white' : 'bg-white border border-gray-300'
                    }`}>
                      {(filters as any)[feature.key] && <Check className="w-3 h-3" />}
                    </span>
                    <span className="text-sm font-medium text-gray-700">{feature.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </details>

          {/* Bottom spacer so content doesn't hide behind sticky footer */}
          <div className="h-20" />
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t px-4 py-3 safe-bottom bg-white">
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-none h-11 px-4"
              onClick={() => onOpenChange(false)}
            >
              Annulla
            </Button>
            <Button
              className="flex-1 h-11 text-base font-semibold"
              onClick={handleSave}
              disabled={isSaving || !filters.city?.trim()}
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creazione...
                </>
              ) : (
                <>
                  <Bell className="w-4 h-4 mr-2" />
                  Crea ricerca con notifiche
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
